import { tap, switchMap, debounceTime } from "rxjs/operators";
import { formOperation } from "../../wrappers/formOperation";
import { TActionsDefinitionsList } from "./TAction";
import { action, routeAction } from "./actionFunctions";
import { TTriageEmailMarkAsResolvedForm, TriageForm } from "../../../../domain/codecs/form/TriageForm";
import { Observable, merge, from } from "rxjs";
import {
    createTriageAssignToCaseAndHandler$,
    createTriageAssignToCaseAndMe$,
    createTriageAssignToCaseAndResolve$,
    createTriageAssignToCaseAndStaff$,
    createTriageLoadMore$,
    createTriageOpenEmail$,
    createTriageResolve$,
    createTriageSearchCase$,
    createTriageAssignToPerson$,
    triageReload$,
} from "./crmTriageActions";
import { requireExhaustive } from "../../../../shared/src/util";

export const actions: TActionsDefinitionsList = [
    routeAction("VIEW_CRM_LEGAL_INBOX", (obs$, lens, set, get) => {
        obs$.pipe(
            tap(() => set(lens.inbox_assignee_page.set({
                resolved: {
                    ...TriageForm.newDefault(),
                    edited: {
                        offset: 0,
                        limit: 10,
                    },
                },
                unresolved: {
                    ...TriageForm.newDefault(),
                    edited: {
                        offset: 0,
                        limit: 10,
                    },
                },
                confidential: {
                    ...TriageForm.newDefault(),
                    edited: {
                        offset: 0,
                        limit: 10,
                    },
                },
                untriaged: {
                    ...TriageForm.newDefault(),
                    edited: {
                        offset: 0,
                        limit: 10,
                    },
                },
                active_type_visible: "UNRESOLVED",
                open_email_id: null,
                right_column_compose_override: false,
            }))),
            switchMap(() =>
                merge(
                    from(formOperation("GetAssigneeUnresolvedEmails", lens.inbox_assignee_page.unresolved.get()(get()))).pipe(
                        tap((unresolved) => set(lens.inbox_assignee_page.unresolved.set(unresolved))),
                    ),
                    from(formOperation("GetAssigneeResolvedEmails", lens.inbox_assignee_page.resolved.get()(get()))).pipe(
                        tap((resolved) => set(lens.inbox_assignee_page.resolved.set(resolved))),
                    ),
                    from(formOperation("GetConfidentialEmails", lens.inbox_assignee_page.confidential.get()(get()))).pipe(
                        tap((confidential) => set(lens.inbox_assignee_page.confidential.set(confidential))),
                    ),
                    from(formOperation("GetUserUntriagedEmails", lens.inbox_assignee_page.untriaged.get()(get()))).pipe(
                        tap((untriaged) => set(lens.inbox_assignee_page.untriaged.set(untriaged))),
                    ),
                )
            ),
        ).subscribe();
    }),

    // ACTIVE TYPE VISIBLE CHANGE
    action("TRIAGE_ASSIGNEE_ACTIVE_TYPE_VISIBLE_CHANGE", (obs$, lens, setState, getState) => {
        obs$.pipe(
            tap((activeType) => setState(lens.inbox_assignee_page.active_type_visible.set(activeType))),
            switchMap((activeType) =>
                activeType === "UNRESOLVED" ? triageReload$(lens.inbox_assignee_page.unresolved, "GetAssigneeUnresolvedEmails", setState, getState)
                : activeType === "RESOLVED" ? triageReload$(lens.inbox_assignee_page.resolved, "GetAssigneeResolvedEmails", setState, getState)
                : activeType === "CONFIDENTIAL" ? triageReload$(lens.inbox_assignee_page.confidential, "GetConfidentialEmails", setState, getState)
                : activeType === "UNTRIAGED" ? triageReload$(lens.inbox_assignee_page.untriaged, "GetUserUntriagedEmails", setState, getState)
                : requireExhaustive(activeType),
            ),
        ).subscribe();
    }),

    // SEARCH
    action("TRIAGE_ASSIGNEE_SEARCH_CHANGE", (obs$: Observable<string>, lens, setState, getState) => {
        obs$.pipe(
            tap((searchTerm) => {
                const activeType = lens.inbox_assignee_page.active_type_visible.get()(getState());
                const activeLens =
                    activeType === "UNRESOLVED" ? lens.inbox_assignee_page.unresolved
                    : activeType === "RESOLVED" ? lens.inbox_assignee_page.resolved
                    : activeType === "CONFIDENTIAL" ? lens.inbox_assignee_page.confidential
                    : lens.inbox_assignee_page.untriaged;
                setState(activeLens.edited.search_term.set(searchTerm));
            }),
            debounceTime(1000),
            switchMap(() => {
                const activeType = lens.inbox_assignee_page.active_type_visible.get()(getState());
                const activeLens =
                    activeType === "UNRESOLVED" ? lens.inbox_assignee_page.unresolved
                    : activeType === "RESOLVED" ? lens.inbox_assignee_page.resolved
                    : activeType === "CONFIDENTIAL" ? lens.inbox_assignee_page.confidential
                    : lens.inbox_assignee_page.untriaged;
                const formOperationName =
                    activeType === "UNRESOLVED" ? "GetAssigneeUnresolvedEmails" as const
                    : activeType === "RESOLVED" ? "GetAssigneeResolvedEmails" as const
                    : activeType === "CONFIDENTIAL" ? "GetConfidentialEmails" as const
                    : "GetUserUntriagedEmails" as const;
                return formOperation(formOperationName, {
                    ...activeLens.get()(getState()),
                    children: {
                        ...activeLens.get()(getState()).children,
                        emails: [],
                    }
                });
            }),
            tap((response) => {
                const activeType = lens.inbox_assignee_page.active_type_visible.get()(getState());
                const activeLens =
                    activeType === "UNRESOLVED" ? lens.inbox_assignee_page.unresolved
                    : activeType === "RESOLVED" ? lens.inbox_assignee_page.resolved
                    : activeType === "CONFIDENTIAL" ? lens.inbox_assignee_page.confidential
                    : lens.inbox_assignee_page.untriaged;
                setState(activeLens.set(response));
            }),
        ).subscribe();
    }),

    action("TRIAGE_ASSIGNEE_SET_BAND", (obs$: Observable<string>, lens, setState, getState) => {
        obs$.pipe(
            tap((importance_band) => {
                const activeType = lens.inbox_assignee_page.active_type_visible.get()(getState());
                const activeLens =
                    activeType === "UNRESOLVED" ? lens.inbox_assignee_page.unresolved
                    : activeType === "RESOLVED" ? lens.inbox_assignee_page.resolved
                    : activeType === "CONFIDENTIAL" ? lens.inbox_assignee_page.confidential
                    : lens.inbox_assignee_page.untriaged;
                setState(activeLens.edited.set({
                    ...activeLens.edited.get()(getState()),
                    offset: 0,
                    importance_band,
                }));
            }),
            switchMap(() => {
                const activeType = lens.inbox_assignee_page.active_type_visible.get()(getState());
                const activeLens =
                    activeType === "UNRESOLVED" ? lens.inbox_assignee_page.unresolved
                    : activeType === "RESOLVED" ? lens.inbox_assignee_page.resolved
                    : activeType === "CONFIDENTIAL" ? lens.inbox_assignee_page.confidential
                    : lens.inbox_assignee_page.untriaged;
                const formOperationName =
                    activeType === "UNRESOLVED" ? "GetAssigneeUnresolvedEmails" as const
                    : activeType === "RESOLVED" ? "GetAssigneeResolvedEmails" as const
                    : activeType === "CONFIDENTIAL" ? "GetConfidentialEmails" as const
                    : "GetUserUntriagedEmails" as const;
                return formOperation(formOperationName, {
                    ...activeLens.get()(getState()),
                    children: {
                        ...activeLens.get()(getState()).children,
                        emails: [],
                    }
                });
            }),
            tap((response) => {
                const activeType = lens.inbox_assignee_page.active_type_visible.get()(getState());
                const activeLens =
                    activeType === "UNRESOLVED" ? lens.inbox_assignee_page.unresolved
                    : activeType === "RESOLVED" ? lens.inbox_assignee_page.resolved
                    : activeType === "CONFIDENTIAL" ? lens.inbox_assignee_page.confidential
                    : lens.inbox_assignee_page.untriaged;
                setState(activeLens.set(response));
            }),
        ).subscribe();
    }),

    // OPEN EMAIL
    action("TRIAGE_ASSIGNEE_UNRESOLVED_OPEN_EMAIL", (obs$, lens, setState, getState) => {
        createTriageOpenEmail$(
            obs$.pipe(tap((emailForm) => setState(lens.inbox_assignee_page.open_email_id.set(emailForm.original.id)))),
            lens.inbox_assignee_page.unresolved,
            setState,
            getState,
        ).subscribe();
    }),
    action("TRIAGE_ASSIGNEE_RESOLVED_OPEN_EMAIL", (obs$, lens, setState, getState) => {
        createTriageOpenEmail$(
            obs$.pipe(tap((emailForm) => setState(lens.inbox_assignee_page.open_email_id.set(emailForm.original.id)))),
            lens.inbox_assignee_page.resolved,
            setState,
            getState,
        ).subscribe();
    }),
    action("TRIAGE_ASSIGNEE_UNTRIAGED_OPEN_EMAIL", (obs$, lens, setState, getState) => {
        createTriageOpenEmail$(
            obs$.pipe(tap((emailForm) => setState(lens.inbox_assignee_page.open_email_id.set(emailForm.original.id)))),
            lens.inbox_assignee_page.untriaged,
            setState,
            getState,
        ).subscribe();
    }),
    action("TRIAGE_ASSIGNEE_CONFIDENTIAL_OPEN_EMAIL", (obs$, lens, setState, getState) => {
        createTriageOpenEmail$(
            obs$.pipe(tap((emailForm) => setState(lens.inbox_assignee_page.open_email_id.set(emailForm.original.id)))),
            lens.inbox_assignee_page.confidential,
            setState,
            getState,
        )
            .pipe(
                // SET THE EMAIL AS READ
                tap((form) => setState(lens.inbox_assignee_page.confidential.children.emails.where((f) => f.edited.id === form.edited.id).children.is_read.set(true))),
            )
            .subscribe();
    }),

    // LOAD MORE
    action("TRIAGE_ASSIGNEE_UNRESOLVED_LOAD_MORE", (obs$, lens, setState, getState) => {
        createTriageLoadMore$(obs$, lens.inbox_assignee_page.unresolved, "GetAssigneeUnresolvedEmails", setState, getState).subscribe();
    }),
    action("TRIAGE_ASSIGNEE_RESOLVED_LOAD_MORE", (obs$, lens, setState, getState) => {
        createTriageLoadMore$(obs$, lens.inbox_assignee_page.resolved, "GetAssigneeResolvedEmails", setState, getState).subscribe();
    }),
    action("TRIAGE_ASSIGNEE_UNTRIAGED_LOAD_MORE", (obs$, lens, setState, getState) => {
        createTriageLoadMore$(obs$, lens.inbox_assignee_page.untriaged, "GetUserUntriagedEmails", setState, getState).subscribe();
    }),
    action("TRIAGE_ASSIGNEE_CONFIDENTIAL_LOAD_MORE", (obs$, lens, setState, getState) => {
        createTriageLoadMore$(obs$, lens.inbox_assignee_page.confidential, "GetConfidentialEmails", setState, getState).subscribe();
    }),

    // SEARCH CASE
    action("TRIAGE_ASSIGNEE_UNRESOLVED_SEARCH_CASE", (obs$, lens, setState) => {
        createTriageSearchCase$(obs$, lens.inbox_assignee_page.unresolved.children.emails, setState).subscribe();
    }),
    action("TRIAGE_ASSIGNEE_RESOLVED_SEARCH_CASE", (obs$, lens, setState) => {
        createTriageSearchCase$(obs$, lens.inbox_assignee_page.resolved.children.emails, setState).subscribe();
    }),
    action("TRIAGE_ASSIGNEE_UNTRIAGED_SEARCH_CASE", (obs$, lens, setState) => {
        createTriageSearchCase$(obs$, lens.inbox_assignee_page.untriaged.children.emails, setState).subscribe();
    }),

    // ASSIGN CASE AND ME
    action("TRIAGE_ASSIGNEE_UNRESOLVED_ASSIGN_TO_CASE_AND_ME", (obs$, lens, setState, getState) => {
        createTriageAssignToCaseAndMe$(obs$, lens.inbox_assignee_page.unresolved, setState, getState).subscribe();
    }),
    action("TRIAGE_ASSIGNEE_UNTRIAGED_ASSIGN_TO_CASE_AND_ME", (obs$, lens, setState, getState) => {
        createTriageAssignToCaseAndMe$(obs$, lens.inbox_assignee_page.untriaged, setState, getState).subscribe();
    }),

    // ASSIGN CASE AND OTHER STAFF
    action("TRIAGE_ASSIGNEE_UNRESOLVED_ASSIGN_TO_CASE_AND_STAFF", (obs$, lens, setState, getState) => {
        createTriageAssignToCaseAndStaff$(obs$, lens.inbox_assignee_page.unresolved, setState, getState).subscribe();
    }),
    action("TRIAGE_ASSIGNEE_UNTRIAGED_ASSIGN_TO_CASE_AND_STAFF", (obs$, lens, setState, getState) => {
        createTriageAssignToCaseAndStaff$(obs$, lens.inbox_assignee_page.untriaged, setState, getState).subscribe();
    }),

    // ASSIGN CASE AND HANDLER
    action("TRIAGE_ASSIGNEE_UNRESOLVED_ASSIGN_TO_CASE_AND_HANDLER", (obs$, lens, setState, getState) => {
        createTriageAssignToCaseAndHandler$(obs$, lens.inbox_assignee_page.unresolved, setState, getState).subscribe();
    }),
    action("TRIAGE_ASSIGNEE_UNTRIAGED_ASSIGN_TO_CASE_AND_HANDLER", (obs$, lens, setState, getState) => {
        createTriageAssignToCaseAndHandler$(obs$, lens.inbox_assignee_page.untriaged, setState, getState).subscribe();
    }),

    // ASSIGN CASE AND RESOLVE
    action("TRIAGE_ASSIGNEE_UNRESOLVED_ASSIGN_TO_CASE_AND_RESOLVE", (obs$, lens, setState, getState) => {
        createTriageAssignToCaseAndResolve$(obs$, lens.inbox_assignee_page.unresolved, setState, getState).subscribe();
    }),
    action("TRIAGE_ASSIGNEE_UNTRIAGED_ASSIGN_TO_CASE_AND_RESOLVE", (obs$, lens, setState, getState) => {
        createTriageAssignToCaseAndResolve$(obs$, lens.inbox_assignee_page.untriaged, setState, getState).subscribe();
    }),

    // ASSIGN TO PERSON
    action("TRIAGE_ASSIGNEE_UNRESOLVED_ASSIGN_TO_PERSON", (obs$, lens, setState, getState) => {
        createTriageAssignToPerson$(obs$, lens.inbox_assignee_page.unresolved, setState, getState).subscribe();
    }),
    action("TRIAGE_ASSIGNEE_UNTRIAGED_ASSIGN_TO_PERSON", (obs$, lens, setState, getState) => {
        createTriageAssignToPerson$(obs$, lens.inbox_assignee_page.untriaged, setState, getState).subscribe();
    }),

    // RESOLVE EMAIL
    action("TRIAGE_ASSIGNEE_RESOLVE", (obs$, lens, setState, getState) => {
        createTriageResolve$(obs$, lens.inbox_assignee_page.unresolved, setState, getState).subscribe();
    }),
    // RESOLVE EMAIL - UPDATE CHASE MODE FORM
    action("TRIAGE_ASSIGNEE_RESOLVE_FORM_UPDATE_CHASE_MODE_FORM", (obs$: Observable<TTriageEmailMarkAsResolvedForm>, lens, setState, getState) => {
        obs$.pipe(
            tap((payload) => setState(lens.inbox_assignee_page.unresolved.children.emails
                .where((f) => f.edited.id === payload.edited.email_id)
                .children.detailed_email_form.children.mark_as_resolved_form
                .set({
                    ...payload,
                    status: "requiresSubmission",
                }),
            ))
        ).subscribe()
    }),

    action("TRIAGE_ASSIGNEE_SET_RIGHT_PANE_COMPOSE", (obs$: Observable<boolean>, lens, setState) => {
        obs$.pipe(
            tap((next) => setState(lens.inbox_assignee_page.right_column_compose_override.set(next))),
        ).subscribe();
    }),
];
